import React, { Fragment, Suspense, useEffect, useState, lazy, memo } from 'react';
import { ISiteObjectViewModel, ISiteObjectType } from '@monofe/ui-workspace/siteobject/interface';
import placeholderData from '@monofe/ui-workspace/siteobject/placeholderData';
import { IRouteSiteObjectArguments, ISiteObjectControllerProps } from './interface';
import LoadingView from '@monofe/ui-workspace/common/Loading';
import type IMapping from './mapping';
import { injectLog } from '../../interface/util';
import { getProjectOverrides } from '../../interface/overrides';

const MainView = lazy(() => import('@monofe/ui-workspace/siteobject'));
const loadMapper = (): Promise<typeof IMapping> => {
    return new Promise(function (resolve, reject) {
        import('./mapping')
            .then(controller => resolve(controller.default))
            .catch(error => reject(error));
    });
};

const RouteEqual = (
    route1: Partial<IRouteSiteObjectArguments>,
    route2: Partial<IRouteSiteObjectArguments>,
): boolean => {
    if (route1.db !== route2.db) {
        return false;
    }
    if (route1.sessionName !== route2.sessionName) {
        return false;
    }
    if (route1.languageId !== route2.languageId) {
        return false;
    }
    if (route1.siteObjectId !== route2.siteObjectId) {
        return false;
    }
    if (route1.tourId !== route2.tourId) {
        return false;
    }
    return true;
};

interface ISiteObjectController {
    model: ISiteObjectViewModel;
    type: ISiteObjectType;
    isLoading: boolean;
}

function View(props: ISiteObjectControllerProps): JSX.Element {
    const log = injectLog();

    const [state, setState] = useState<ISiteObjectController>({
        model: placeholderData(),
        type: ISiteObjectType.Image,
        isLoading: true,
    });

    useEffect(() => {
        loadMapper()
            .then(function (mapper) {
                return mapper(props.route, props.didRequestBack);
            })
            .then(function ([model, type]) {
                return setState(stateIn => ({
                    ...stateIn,
                    props,
                    model: getProjectOverrides<ISiteObjectViewModel>('ISiteObjectViewModel', model),
                    type,
                    isLoading: false,
                }));
            })
            .then(_ => log.info('Loaded SiteObject'))
            .catch(error => {
                log.error(`Failed to load SiteObject, loading previous page. Error:${error}`);
                props.didRequestBack();
            });
    }, [props, props.route]);

    useEffect(() => {
        /* Markdown: Remove emphasis (repeat the line to remove double emphasis) */
        document.title = state.model.title.text
            .replace(/([\*_]{1,3})(\S.*?\S{0,1})\1/g, '$2')
            .replace(/([\*_]{1,3})(\S.*?\S{0,1})\1/g, '$2');
    }, [state.model.title.text]);

    return (
        <>
            {state.isLoading ? (
                <LoadingView
                    styleOverrides={{
                        opacity: 1,
                        pointerEvents: 'auto',
                    }}
                />
            ) : (
                <Suspense fallback={null}>
                    <MainView key="SiteObject" type={state.type} model={state.model} />
                </Suspense>
            )}
        </>
    );
}

const propsEqual = (prev: ISiteObjectControllerProps, next: ISiteObjectControllerProps) => {
    return RouteEqual(prev.route, next.route);
};

export default memo(View, propsEqual) as typeof View;
